// Cómo el paso 03 describe una promoción que nombra a un SKU: qué da y a quién. Acá ya hay plan y
// precio, así que los efectos van CON su cifra (a diferencia del banner de tienda, `scopeWideEffect`).
import { ArrowDown, ArrowUp, CreditCard, Percent, type LucideIcon } from 'lucide-react';
import { formatMXN } from '@/shared/lib/format/money';
import type { PlanLevel, PromotionEffect, SkuPromotion } from './types';

/** Etiqueta del nivel, como la nombra el vendedor. */
const LEVEL_LABEL: Record<PlanLevel, string> = {
    BRONZE: 'Bronce',
    SILVER: 'Plata',
    GOLD: 'Oro',
};

export function levelLabel(level: PlanLevel): string {
    return LEVEL_LABEL[level];
}

/**
 * Si la promoción alcanza al cliente. `undefined` = el score todavía no resolvió nivel, así que
 * ninguna se descarta; con nivel resuelto (o `null` = NOT_ELIGIBLE) solo quedan las abiertas y las
 * suyas — anunciar la de otro nivel es prometer lo que no le va a tocar.
 */
export function reachesLevel(
    targetLevels: readonly PlanLevel[],
    clientLevel: PlanLevel | null | undefined,
): boolean {
    if (targetLevels.length === 0) return true;
    if (clientLevel === undefined) return true;
    return clientLevel !== null && targetLevels.includes(clientLevel);
}

/** Un efecto de la promoción listo para pintarse como píldora. */
export interface PromoEffectChip {
    /** Campo que mueve — sirve de `key` de React y de criterio de prioridad. */
    key: 'downPayment' | 'tna' | 'bonus' | 'maxAmount';
    icon: LucideIcon;
    /** Frase completa, para el panel de detalle. */
    label: string;
    /** Versión corta, para la tarjeta del grid. */
    short: string;
}

/**
 * Qué hace la promoción, en píldoras que el vendedor puede leerle al cliente. Una promoción puede
 * mover varias palancas, así que devuelve una lista y no una sola.
 */
export function effectChips(effect: PromotionEffect): PromoEffectChip[] {
    const chips: PromoEffectChip[] = [];
    const { downPaymentDeltaPct, pcfBonusCents, tnaOverride, maxAmountOverrideCents } = effect;

    if (typeof downPaymentDeltaPct === 'number' && downPaymentDeltaPct !== 0) {
        // Negativo = BAJA el enganche. Se dice en puntos, que es como se habla del enganche.
        const puntos = String(Math.abs(Math.round(downPaymentDeltaPct * 100)));
        const baja = downPaymentDeltaPct < 0;
        chips.push({
            key: 'downPayment',
            icon: baja ? ArrowDown : ArrowUp,
            label: baja ? `Baja el enganche ${puntos} puntos` : `Sube el enganche ${puntos} puntos`,
            short: `${baja ? '−' : '+'}${puntos} pts de enganche`,
        });
    }
    if (typeof tnaOverride === 'number' && tnaOverride > 0) {
        const pct = String(Math.round(tnaOverride * 100));
        chips.push({
            key: 'tna',
            icon: Percent,
            label: `Tasa anual de ${pct}%`,
            short: `Tasa ${pct}%`,
        });
    }
    if (typeof pcfBonusCents === 'number' && pcfBonusCents > 0) {
        const monto = formatMXN(pcfBonusCents / 100);
        chips.push({
            key: 'bonus',
            icon: CreditCard,
            label: `Bono de ${monto} a tu cuenta`,
            short: `Bono de ${monto}`,
        });
    }
    if (typeof maxAmountOverrideCents === 'number' && maxAmountOverrideCents > 0) {
        const monto = formatMXN(maxAmountOverrideCents / 100);
        chips.push({
            key: 'maxAmount',
            icon: ArrowUp,
            label: `Financia hasta ${monto}`,
            short: `Financia hasta ${monto}`,
        });
    }
    return chips;
}

/** Orden en que compite un efecto por el único lugar de la tarjeta: lo que traba la venta primero. */
const HEADLINE_ORDER: PromoEffectChip['key'][] = ['downPayment', 'tna', 'bonus', 'maxAmount'];

/** El efecto que la tarjeta anuncia, y cuántos quedaron sin anunciar. */
export interface PromoHeadline {
    chip: PromoEffectChip;
    /** Efectos restantes, para el `+N` — nunca dos píldoras en una tarjeta de 150 px. */
    extra: number;
}

/**
 * Lo que la tarjeta del grid pinta: un solo efecto, el que más mueve la decisión en el mostrador.
 * `null` cuando ninguna promoción alcanza al cliente — la tarjeta entonces no anuncia nada.
 */
export function headlineChip(
    promotions: readonly SkuPromotion[],
    clientLevel: PlanLevel | null | undefined,
): PromoHeadline | null {
    const chips = promotions
        .filter((p) => reachesLevel(p.targetLevels, clientLevel))
        .flatMap((p) => effectChips(p.effect));
    if (chips.length === 0) return null;

    const winner = HEADLINE_ORDER.map((key) => chips.find((c) => c.key === key)).find(
        (c) => c !== undefined,
    );
    if (!winner) return null;
    return { chip: winner, extra: chips.length - 1 };
}
